import { ApiClient } from '../../ApiClient';
import { ConnectionDTO } from '../dto/MarketplaceDTOs';
import { FullSyncJob } from '@domain/connection/models/ImportJob';
import { AmazonRegion } from '@domain/connection/IMarketplaceRepository';
import { AmazonProductMatchMapping, AmazonSettings } from '@domain/connection/models/ConnectionSettings';
import { AmazonMarketplaceOption, AmazonMatchPreviewResponse, CompleteAmazonSetupInput, CompleteAmazonSetupResponse, FinishAmazonSetupInput } from '@domain/connection/models/AmazonMatchPreview';

/** One marketplace list per connection. Sellers participations are rate limited, so repeat opens reuse this. */
const marketplaceLists = new Map<string, Promise<AmazonMarketplaceOption[]>>();

export class AmazonConnectorDataSource {
  constructor(private api: ApiClient) {
  }

  async startAuthorization(region: AmazonRegion, connectionId?: string): Promise<{ authUrl: string; state: string; redirectUri: string }> {
    const response = await this.api.post<{ authUrl: string; state: string; redirectUri: string }>('/connections/amazon/authorize', {
      region,
      connectionId,
    });
    return response.data;
  }

  async connectSandbox(region: AmazonRegion, connectionId?: string): Promise<ConnectionDTO> {
    const response = await this.api.post<ConnectionDTO>('/connections/amazon/sandbox', {
      region,
      connectionId,
    });
    return response.data;
  }

  async completeAuthorization(code: string, state: string, sellingPartnerId?: string): Promise<ConnectionDTO> {
    const response = await this.api.post<ConnectionDTO>('/connections/amazon/complete', {
      code,
      state,
      sellingPartnerId,
    });
    return response.data;
  }

  async syncProducts(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>('/connections/amazon/products/sync', null, { params: { connectionId } });
    return response.data;
  }

  async syncInventory(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>('/connections/amazon/inventory/sync', null, { params: { connectionId } });
    return response.data;
  }

  async syncAllOrders(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>('/connections/amazon/orders/sync/all', null, { params: { connectionId } });
    return response.data;
  }

  async syncNewOrders(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>('/connections/amazon/orders/sync/new', null, { params: { connectionId } });
    return response.data;
  }

  async syncSearchTerms(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>('/connections/amazon/insights/search-terms/sync', null, { params: { connectionId } });
    return response.data;
  }

  async getSettings(connectionId: string): Promise<AmazonSettings> {
    const response = await this.api.get<AmazonSettings>(`/connections/${connectionId}/settings`);
    return response.data;
  }

  async updateSettings(connectionId: string, settings: Partial<AmazonSettings>): Promise<AmazonSettings> {
    const response = await this.api.put<AmazonSettings>(`/connections/${connectionId}/settings`, settings);
    return response.data;
  }

  listMarketplaces(connectionId: string): Promise<AmazonMarketplaceOption[]> {
    const cached = marketplaceLists.get(connectionId);
    if (cached) return cached;

    const request = this.api.get<AmazonMarketplaceOption[]>(`/connections/amazon/${connectionId}/marketplaces`)
      .then(response => response.data)
      .catch(error => {
        marketplaceLists.delete(connectionId);
        throw error;
      });
    marketplaceLists.set(connectionId, request);
    return request;
  }

  async saveMarketplaces(connectionId: string, marketplaceIds: string[]): Promise<void> {
    await this.api.post(`/connections/amazon/${connectionId}/marketplaces`, { marketplaceIds });
    const cached = marketplaceLists.get(connectionId);
    if (!cached) return;
    marketplaceLists.set(connectionId, cached.then(rows => rows.map(row => ({
      ...row,
      selected: marketplaceIds.includes(row.id),
    }))));
  }

  async getMatchPreview(
    connectionId: string,
    mapping?: Partial<AmazonProductMatchMapping>,
    pageToken?: string,
    titleCountryCode?: string,
  ): Promise<AmazonMatchPreviewResponse> {
    const response = await this.api.post<AmazonMatchPreviewResponse>(
      `/connections/amazon/${connectionId}/match-preview`,
      {
        sourceField: mapping?.sourceField,
        targetField: mapping?.targetField,
        pageToken,
        titleCountryCode,
      },
    );
    return response.data;
  }

  async completeSetup(
    connectionId: string,
    input: CompleteAmazonSetupInput,
  ): Promise<CompleteAmazonSetupResponse> {
    const response = await this.api.post<CompleteAmazonSetupResponse>(
      `/connections/amazon/${connectionId}/complete-setup`,
      input,
    );
    return response.data;
  }

  async finishSetup(
    connectionId: string,
    input: FinishAmazonSetupInput,
  ): Promise<CompleteAmazonSetupResponse> {
    const response = await this.api.post<CompleteAmazonSetupResponse>(
      `/connections/amazon/${connectionId}/finish-setup`,
      input,
    );
    return response.data;
  }

  async syncFull(connectionId: string): Promise<FullSyncJob[]> {
    const response = await this.api.post<{ jobs: FullSyncJob[] }>('/connections/amazon/sync/full', null, { params: { connectionId } });
    return response.data.jobs;
  }
}
